<template>
  <el-container class="home-page-container">
    <!-- 左侧边栏 -->
    <el-aside
      :width="asideWidth"
      class="page-aside"
      :class="{ collapsed: isSidebarCollapsed }"
    >
      <div class="sidebar-title" :class="{ collapsed: isSidebarCollapsed }">
        <template v-if="isSidebarCollapsed">
          <div class="icon-buttons-group">
            <i class="el-icon-s-grid"></i>
            <el-tooltip
              v-if="isLoggedIn"
              :content="$t('sidebar.historyRecord')"
              placement="right"
              effect="dark"
            >
              <i
                class="el-icon-tickets history-icon"
                @click="toggleReportPanel()"
              ></i>
            </el-tooltip>
            <el-tooltip
              :content="$t('sidebar.expand')"
              placement="right"
              effect="dark"
            >
              <i
                class="el-icon-arrow-left collapse-icon"
                :class="{ rotated: isSidebarCollapsed }"
                @click="toggleSidebar"
              ></i>
            </el-tooltip>
          </div>
        </template>
        <template v-else>
          <div class="title-left-group">
            <i class="el-icon-s-grid"></i>
            <span>{{ $t("sidebar.title") }}</span>
          </div>
          <div class="title-right-group">
            <el-tooltip
              v-if="isLoggedIn"
              :content="$t('sidebar.historyRecord')"
              placement="top"
              effect="dark"
            >
              <i
                class="el-icon-tickets history-icon"
                @click="toggleReportPanel()"
              ></i>
            </el-tooltip>
            <el-tooltip
              :content="$t('sidebar.collapse')"
              placement="top"
              effect="dark"
            >
              <i
                class="el-icon-arrow-left collapse-icon"
                @click="toggleSidebar"
              ></i>
            </el-tooltip>
          </div>
        </template>
      </div>

      <div class="mobile-sidebar-header">
        <el-tooltip
          v-if="isLoggedIn"
          :content="$t('sidebar.historyRecord')"
          placement="top"
          effect="dark"
        >
          <i
            class="el-icon-tickets history-icon"
            @click="toggleReportPanel()"
          ></i>
        </el-tooltip>
        <el-tooltip
          :content="
            isSidebarCollapsed ? $t('sidebar.expand') : $t('sidebar.collapse')
          "
          placement="top"
          effect="dark"
        >
          <i
            class="el-icon-arrow-up collapse-icon mobile-collapse-icon"
            :class="{ rotated: isSidebarCollapsed }"
            @click="toggleSidebar"
          ></i>
        </el-tooltip>
      </div>

      <ul class="reference-list" v-show="!isSidebarCollapsed">
        <li
          v-for="chart in visibleCharts"
          :key="chart.id"
          class="reference-item"
          :class="{ 'active-bg': activeChartId === chart.id, active: true }"
        >
          <div class="img-wrapper">
            <img :src="chart.image" :alt="chart.alt" />
          </div>
          <div class="item-title">
            <span>{{ $t(chart.titleKey) }}</span>
          </div>
        </li>
      </ul>
    </el-aside>

    <!-- 主要内容区域 -->
    <el-main class="page-main">
      <!-- 主功能区 (视图 A 和 B) -->
      <template v-if="!showReportPanel">
        <!-- 视图A: 上传区域 -->
        <div
          v-if="
            !analysisResult ||
            !analysisResult.results ||
            analysisResult.results.length === 0
          "
          class="main-content"
          @click.capture="handleUploadAreaClick"
        >
          <div class="main-content-inner">
            <div class="title-section">
              <img
                src="@/assets/riqa-logo.png"
                alt="RIQA Logo"
                class="riqa-logo"
              />
              <h1>{{ $t("main.title") }}</h1>
            </div>
            <p class="description">{{ $t("main.description") }}</p>

            <el-upload
              ref="uploadArea"
              class="upload-area"
              drag
              action=""
              multiple
              :show-file-list="false"
              :limit="3"
              :http-request="uploadHttpRequest"
              :before-upload="beforeUpload"
              :on-change="handleFileChange"
              :on-exceed="handleExceed"
            >
              <div class="upload-text-primary">
                {{ $t("main.uploadArea.dragText") }}
              </div>
              <div class="upload-text-secondary">
                {{ $t("main.uploadArea.supportedFormats") }}
              </div>

              <div class="upload-actions-wrapper">
                <div class="custom-upload-button">
                  <i class="el-icon-upload2"></i>
                  <span>{{ $t("main.uploadArea.buttonText") }}</span>
                </div>
              </div>

              <div v-if="isLoggedIn" class="upload-usage-hint">
                {{ $t("main.uploadArea.uploadVolume") }}{{ imageUploadCount }} /
                {{ maxUploadPerDay }}
              </div>
            </el-upload>
          </div>

          <footer class="page-footer">
            <a
              v-if="$t('footer.beian')"
              :href="'https://beian.miit.gov.cn/'"
              target="_blank"
              rel="noopener noreferrer"
              class="beian-link"
            >
              {{ $t("footer.beian") }}
            </a>
            <span v-if="$t('footer.beian') && $t('footer.copyright')"> | </span>
            {{ $t("footer.copyright") }}
          </footer>
        </div>

        <!-- 视图B: 分析面板 -->
        <analysis-panel
          v-else
          :images="analysisResult.results"
          :image-upload-count="imageUploadCount"
          :max-upload-per-day="maxUploadPerDay"
          @category-change="setActiveChartByCategoryName"
          @image-deleted="handleImageDeleted"
          @add-images="handleNewImages"
          @analysis-submitted="handleAnalysisSubmitted"
        ></analysis-panel>
      </template>

      <!-- 报告面板视图 -->
      <report-panel
        v-else
        :reports="reportList"
        :is-loading-list="isReportListLoading"
        :image-upload-count="imageUploadCount"
        :max-upload-per-day="maxUploadPerDay"
        @add-images="handleTriggerUpload"
        @report-selected="handleReportSelection"
        @download-state-change="handlePdfDownloadState"
        @refresh-reports="handleRefreshReports"
      ></report-panel>
    </el-main>

    <!-- 加载层 -->
    <task-progress-overlay
      :visible="isLoading"
      :progress="progress"
      :message="progressMessage"
    />
  </el-container>
</template>

<script>
import { mapGetters } from "vuex";
import {
  uploadImagesAndGetTask,
  getTaskProgress,
  getTaskResult,
  getAnalysisHistory,
  getUserInfo,
} from "@/api";
import AnalysisPanel from "@/components/AnalysisPanel.vue";
import ReportPanel from "@/components/ReportPanel.vue";
// 导入新的加载组件
import TaskProgressOverlay from "@/components/TaskProgressOverlay.vue";

export default {
  name: "HomePage",
  components: {
    AnalysisPanel,
    ReportPanel,
    TaskProgressOverlay,
  },
  data() {
    return {
      analysisJustCompleted: false,
      showReportPanel: false,
      isReportListLoading: false,
      reportList: [],
      isLoading: false,
      progress: 0,
      pollTimer: null,
      screenWidth: window.innerWidth,
      isSidebarCollapsed: false,
      charts: [
        {
          id: 1,
          name: "Color Reproduction",
          nameKey: "colorReproduction",
          titleKey: "sidebar.colorReproduction",
          alt: "Color Reproduction",
          image: require("@/assets/color-reproduction.png"),
        },
        {
          id: 2,
          name: "Dynamic Range",
          nameKey: "dynamicRange",
          titleKey: "sidebar.dynamicRange",
          alt: "Dynamic Range",
          image: require("@/assets/dynamic-range.png"),
        },
        {
          id: 3,
          name: "Sharpness",
          nameKey: "sharpness",
          titleKey: "sidebar.sharpness",
          alt: "Sharpness",
          image: require("@/assets/sharpness.png"),
        },
        {
          id: 4,
          name: "Luminance Uniformity",
          nameKey: "luminanceUniformity",
          titleKey: "sidebar.luminanceUniformity",
          alt: "Luminance Uniformity",
          image: require("@/assets/luminance-uniformity.png"),
        },
        {
          id: 5,
          name: "Distortion",
          nameKey: "distortion",
          titleKey: "sidebar.distortion",
          alt: "Distortion",
          image: require("@/assets/distortion.png"),
        },
      ],
      userFeatures: [],
      activeChartId: null,
      analysisResult: null,
      filesToUpload: [],
      imageUploadCount: 0,
      maxUploadPerDay: 0,
      isBatchUploading: false,
      isPdfDownloading: false,
      progressMessage: "",
    };
  },
  computed: {
    ...mapGetters(["isLoggedIn"]),

    asideWidth() {
      if (this.screenWidth <= 768) {
        return null;
      }
      return this.isSidebarCollapsed ? "60px" : "260px";
    },
    progressWidth() {
      return this.screenWidth > 768 ? 198 : 120;
    },
    visibleCharts() {
      if (!this.isLoggedIn) {
        return this.charts;
      }
      if (this.userFeatures.length === 0) {
        return [];
      }
      return this.charts.filter((chart) =>
        this.userFeatures.includes(chart.nameKey)
      );
    },
  },
  watch: {
    showReportPanel(isShowing) {
      if (!isShowing) {
        this.activeChartId = null;
      }
    },
    isLoggedIn: {
      handler(isLoggedIn) {
        if (isLoggedIn) {
          this.fetchUserFeatures();
        } else {
          this.userFeatures = [];
          this.imageUploadCount = 0;
          this.maxUploadPerDay = 0;
          this.analysisResult = null;
          this.showReportPanel = false;
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    },
    async fetchUserFeatures() {
      try {
        const response = await getUserInfo();
        if (response.success && response.data) {
          if (response.data.settings) {
            this.userFeatures =
              response.data.settings.imageAnalysisFeatures || [];
            this.maxUploadPerDay = response.data.settings.maxUploadPerDay || 0;
          }
          this.imageUploadCount = response.data.image_upload_count || 0;
        }
      } catch (error) {
        console.error("Failed to fetch user features:", error);
        this.userFeatures = [];
        this.maxUploadPerDay = 0;
        this.imageUploadCount = 0;
      }
    },

    handleUploadAreaClick(event) {
      if (!this.isLoggedIn) {
        const uploadEl = this.$refs.uploadArea?.$el;
        if (uploadEl && uploadEl.contains(event.target)) {
          this.$emit("request-login");
          event.stopPropagation();
          event.preventDefault();
        }
      }
    },

    checkLoginAndRequest() {
      if (!this.isLoggedIn) {
        this.$emit("request-login");
        return true;
      }
      return false;
    },

    handleAnalysisSubmitted(imageIds) {
      this.analysisJustCompleted = true;
      this.toggleReportPanel(true, imageIds);
    },
    handleReportSelection(analysisType) {
      if (!analysisType) {
        this.activeChartId = null;
        return;
      }
      const selectedChart = this.charts.find(
        (chart) => chart.name === analysisType
      );
      this.activeChartId = selectedChart ? selectedChart.id : null;
    },
    handleTriggerUpload() {
      if (this.checkLoginAndRequest()) return;
      this.analysisResult = null;
      this.showReportPanel = false;
      this.$nextTick(() => {
        this.$refs.uploadArea.$el.querySelector("input").click();
      });
    },

    async handleRefreshReports() {
      if (!this.showReportPanel) return;

      try {
        const response = await getAnalysisHistory(null);
        if (response.success) {
          this.reportList = response.data;
        }
      } catch (error) {
        console.log("刷新报告列表失败", error);
      }
    },

    async toggleReportPanel(forceOpen = false, imageIds = null) {
      if (this.checkLoginAndRequest()) return;

      // 场景：当用户点击图标，想要关闭报告面板时
      if (this.showReportPanel && !forceOpen) {
        this.showReportPanel = false;

        // 检查是否是因为刚刚分析完成才关闭的
        if (this.analysisJustCompleted) {
          this.analysisResult = null;
          this.analysisJustCompleted = false;
        }

        return;
      }

      // 场景：打开报告面板
      this.showReportPanel = true;
      this.isReportListLoading = true;
      this.reportList = [];
      try {
        const response = await getAnalysisHistory(imageIds);
        if (response.success) {
          this.reportList = response.data;
        }
      } catch (error) {
        console.log("请求失败，但错误已被捕获，页面不会崩溃。", error);
        this.reportList = [];
      } finally {
        this.isReportListLoading = false;
      }
    },

    async handleNewImages(fileList) {
      if (this.checkLoginAndRequest()) return;
      if (!fileList || fileList.length === 0) return;
      this.filesToUpload = fileList.map((f) => f.raw);
      await this.handleUpload();
    },

    handleImageDeleted(deletedImageId) {
      if (this.analysisResult && this.analysisResult.results) {
        this.analysisResult.results = this.analysisResult.results.filter(
          (image) => image.image_id !== deletedImageId
        );
        if (this.analysisResult.results.length === 0) {
          this.analysisResult = null;
        }
      }
    },
    handleExceed() {
      this.$notify.warning({
        title: this.$t("error.warningTitle"),
        message: this.$t("error.uploadLimitThree"),
      });
    },
    handleDragOver(event) {
      // 使用 capture 模式更早拦截，检查是否是真正的文件
      const dataTransfer = event.dataTransfer;
      if (!dataTransfer) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // 检查 dataTransfer.items 来判断是否是文件系统文件
      let hasRealFiles = false;
      let hasPageElement = false;

      if (dataTransfer.items && dataTransfer.items.length > 0) {
        // 检查 items 中是否有文件类型
        for (let i = 0; i < dataTransfer.items.length; i++) {
          const item = dataTransfer.items[i];
          const itemType = item.type || "";

          // 检查是否是页面元素拖拽（通常包含 text/html）
          if (
            itemType.includes("text/html") ||
            itemType.includes("text/plain")
          ) {
            hasPageElement = true;
          }

          // 只有 kind === 'file' 且不是从页面拖拽的元素才是真正的文件
          if (item.kind === "file") {
            // 页面元素拖拽时，type 通常是 'text/html' 或 'text/plain'
            // 真正的文件系统文件应该有具体的 MIME 类型（如 image/jpeg）
            if (
              itemType &&
              !itemType.startsWith("text/") &&
              !itemType.startsWith("application/x-moz") &&
              !itemType.includes("text/html")
            ) {
              hasRealFiles = true;
              break;
            }
          }
        }
      }

      // 如果检测到页面元素拖拽，或者没有真正的文件，阻止默认行为
      if (hasPageElement || !hasRealFiles) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    },
    handleDrop(event) {
      // 检查是否是真正的文件系统文件
      const dataTransfer = event.dataTransfer;
      if (!dataTransfer) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // 检查 files 列表
      const files = dataTransfer.files;
      if (!files || files.length === 0) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // 检查 items 来确认是文件系统文件
      let hasRealFiles = false;
      let hasPageElement = false;

      if (dataTransfer.items && dataTransfer.items.length > 0) {
        for (let i = 0; i < dataTransfer.items.length; i++) {
          const item = dataTransfer.items[i];
          const itemType = item.type || "";

          // 检查是否是页面元素拖拽
          if (
            itemType.includes("text/html") ||
            itemType.includes("text/plain")
          ) {
            hasPageElement = true;
          }

          if (item.kind === "file") {
            // 页面元素拖拽时通常会有 'text/html' 类型
            // 真正的文件系统文件应该有具体的 MIME 类型
            if (
              itemType &&
              !itemType.startsWith("text/html") &&
              !itemType.startsWith("application/x-moz") &&
              !itemType.includes("text/html")
            ) {
              hasRealFiles = true;
              break;
            }
          }
        }
      } else {
        // 如果没有 items，但有 files，检查是否是有效的文件对象
        // 页面元素拖拽时，files 可能包含无效的文件对象
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          // 真正的文件应该有 name 和 size
          if (file && file.name && file.size > 0 && file instanceof File) {
            hasRealFiles = true;
            break;
          }
        }
      }

      // 如果检测到页面元素拖拽，或者没有真正的文件，阻止默认行为
      if (hasPageElement || !hasRealFiles) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    },
    handleGlobalDragStart(event) {
      // 阻止页面元素（img, icon等）的拖拽
      const target = event.target;
      if (
        target &&
        (target.tagName === "IMG" ||
          target.tagName === "I" ||
          target.closest("img") ||
          target.closest(".el-icon"))
      ) {
        // 检查是否在上传区域内，如果是，允许拖拽文件
        const uploadEl = this.$refs.uploadArea?.$el;
        if (!uploadEl || !uploadEl.contains(target)) {
          // 阻止页面元素的拖拽
          event.preventDefault();
          return false;
        }
      }
    },
    beforeUpload(file) {
      if (this.checkLoginAndRequest()) {
        return false;
      }

      // 检查是否是真正的文件对象，防止页面元素被拖拽上传
      if (!(file instanceof File) && !(file instanceof Blob)) {
        console.warn("拒绝非文件对象的上传:", file);
        return false;
      }

      // 检查文件是否有有效的 size 属性（真正的文件应该有 size > 0）
      if (!file.size || file.size === 0) {
        console.warn("拒绝无效大小的文件:", file);
        return false;
      }

      // 检查文件是否有 type 属性
      if (!file.type) {
        console.warn("拒绝没有类型的文件:", file);
        return false;
      }

      const supportedTypes = [
        "image/jpeg",
        "image/png",
        "image/bmp",
        "image/tiff",
      ];
      const isTypeValid = supportedTypes.includes(file.type);
      // const isSizeValid = file.size / 1024 / 1024 < 50;
      if (!isTypeValid) {
        this.$notify.warning({
          title: this.$t("error.warningTitle"),
          message: this.$t("error.uploadTypeInvalid"),
        });
        return false;
      }
      // if (!isSizeValid) {
      //   this.$notify.warning({
      //     title: this.$t("error.warningTitle"),
      //     message: this.$t("error.uploadSizeExceed"),
      //   });
      //   return false;
      // }
      return isTypeValid;
    },

    handleFileChange(file, fileList) {
      if (this.checkLoginAndRequest()) {
        if (this.$refs.uploadArea) {
          this.$refs.uploadArea.clearFiles();
        }
        return;
      }
      this.filesToUpload = fileList.map((f) => f.raw);
    },

    uploadHttpRequest() {
      if (this.checkLoginAndRequest()) return;

      if (this.isBatchUploading) {
        return;
      }

      this.isBatchUploading = true;

      this.$nextTick(() => {
        this.handleUpload();
      });
    },

    uploadFileWithProgress(info, file) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", info.upload_url, true);
        xhr.setRequestHeader("Content-Type", info.content_type);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        };
        xhr.onerror = () => {
          reject(new Error("Upload failed due to a network error."));
        };
        xhr.send(file);
      });
    },
    async handleUpload() {
      if (this.filesToUpload.length === 0) {
        this.isBatchUploading = false;
        return;
      }

      this.isLoading = true;
      this.progress = 0;
      this.progressMessage = this.$t("main.analyzing");

      try {
        const payload = { images: this.filesToUpload.map((file) => file.name) };
        const response = await uploadImagesAndGetTask(payload);

        const { task_id, images: uploadInfoList } = response.data;
        const fileMap = new Map(
          this.filesToUpload.map((file) => [file.name, file])
        );
        const uploadPromises = uploadInfoList.map((info) => {
          const file = fileMap.get(info.name);
          if (!file) {
            throw new Error(`File ${info.name} not found.`);
          }
          return this.uploadFileWithProgress(info, file);
        });
        await Promise.all(uploadPromises);
        this.pollTaskProgress(task_id);
        this.fetchUserFeatures();
      } catch (error) {
        console.log("请求失败，但错误已被捕获，页面不会崩溃。", error);
        this.isLoading = false;
        this.isBatchUploading = false;
        this.progressMessage = "";
      } finally {
        if (this.$refs.uploadArea) {
          this.$refs.uploadArea.clearFiles();
        }
        this.filesToUpload = [];
      }
    },
    pollTaskProgress(taskId) {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
      }
      this.pollTimer = setInterval(async () => {
        try {
          const response = await getTaskProgress(taskId);
          const currentProgress = response.data.progress;

          this.progress = currentProgress;
          this.progressMessage = `${this.$t("main.analyzing")}`;

          if (currentProgress >= 100) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.progress = 100;
            await this.fetchFinalResult(taskId);
          }
        } catch (error) {
          console.log("请求失败，但错误已被捕获，页面不会崩溃。", error);
          clearInterval(this.pollTimer);
          this.pollTimer = null;
          this.isLoading = false;
          this.isBatchUploading = false;
          this.progressMessage = "";
        }
      }, 3000);
    },
    async fetchFinalResult(taskId) {
      try {
        const response = await getTaskResult(taskId);
        const newResultData = response.data;
        if (this.analysisResult && this.analysisResult.results) {
          // 获取现有图片的 ID 集合，用于去重
          const existingIds = new Set(
            this.analysisResult.results.map((img) => img.image_id)
          );
          // 过滤掉已存在的图片，只保留新图片
          const newImages = newResultData.results.filter(
            (img) => !existingIds.has(img.image_id)
          );
          // 只添加新图片，避免重复
          if (newImages.length > 0) {
            this.analysisResult.results.push(...newImages);
          }
        } else {
          this.analysisResult = newResultData;
        }
      } catch (error) {
        console.log("请求失败，但错误已被捕获，页面不会崩溃。", error);
        if (!this.analysisResult) {
          this.analysisResult = null;
        }
      } finally {
        this.isLoading = false;
        this.isBatchUploading = false;
        this.progressMessage = "";
      }
    },
    setActiveChartByCategoryName(categoryName) {
      const chart = this.charts.find((c) => c.name === categoryName);
      this.activeChartId = chart ? chart.id : null;
    },
    handleResize() {
      this.screenWidth = window.innerWidth;
    },
    handlePdfDownloadState(isDownloading) {
      this.isPdfDownloading = isDownloading;
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    // 阻止页面元素的拖拽
    document.addEventListener("dragstart", this.handleGlobalDragStart, true);
    // 为上传区域添加拖拽事件处理，防止页面元素被拖拽上传
    this.$nextTick(() => {
      const uploadEl = this.$refs.uploadArea?.$el;
      if (uploadEl) {
        // 使用 capture 模式更早拦截事件
        uploadEl.addEventListener("dragover", this.handleDragOver, true);
        uploadEl.addEventListener("drop", this.handleDrop, true);
      }
    });
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("dragstart", this.handleGlobalDragStart, true);
    // 移除上传区域的拖拽事件监听器
    const uploadEl = this.$refs.uploadArea?.$el;
    if (uploadEl) {
      uploadEl.removeEventListener("dragover", this.handleDragOver, true);
      uploadEl.removeEventListener("drop", this.handleDrop, true);
    }
  },
};
</script>

<style scoped>
/* 阻止页面元素的拖拽 */
:deep(img),
:deep(.el-icon),
:deep(i) {
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  pointer-events: auto;
}

.mobile-sidebar-header {
  display: none;
}
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  gap: 20px;
}
.loading-text {
  font-size: 18px;
  color: #00a0e9;
  font-weight: 500;
}
.home-page-container {
  height: 100%;
  padding-top: 1%;
  box-sizing: border-box;
}
.page-aside {
  border-right: 1px solid #e6ebf5;
  padding: 20px;
  background-color: #fff;
  border-top-right-radius: 6px;
  transition: width 0.3s ease, padding 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.page-aside.collapsed {
  padding: 10px 5px;
}
.sidebar-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.sidebar-title.collapsed {
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 10px;
}
.title-left-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.title-right-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.icon-buttons-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}
.history-icon {
  font-size: 20px;
  color: #606266;
  cursor: pointer;
  transition: color 0.3s;
}
.history-icon:hover {
  color: #00a0e9;
}
.collapse-icon {
  font-size: 20px;
  color: #606266;
  cursor: pointer;
  transition: transform 0.3s, color 0.3s;
}
.collapse-icon:hover {
  color: #00a0e9;
}
.collapse-icon.rotated {
  transform: rotate(180deg);
}
.reference-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
}

.reference-list::-webkit-scrollbar {
  width: 6px;
}

.reference-list::-webkit-scrollbar-track {
  background: transparent;
}

.reference-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.reference-list::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
.reference-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px;
  border-radius: 8px;
  border: 1px solid transparent;
  margin-bottom: 10px;
  transition: background-color 0.3s ease;
}
.reference-item.active-bg {
  background-color: #ebeef5;
}
.img-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 90px;
  width: 100%;
  max-width: 135px;
  border-radius: 4px;
  background-color: #f7f8fa;
  margin-bottom: 5px;
  overflow: hidden;
}
.reference-item img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.item-title {
  position: relative;
  width: 135px;
  padding-bottom: 5px;
}
.item-title span {
  font-size: 12px;
  color: #303133;
}
.item-title::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 24px;
  height: 2px;
  background-color: #00a0e9;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}
.reference-item.active .item-title::after {
  transform: scaleX(1);
}
.page-main {
  display: flex;
  flex-direction: column;
  background-color: #f7f8fa;
  padding: 0;
  height: 100%;
  overflow-x: hidden;
  min-width: 0;
}
.main-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.main-content-inner {
  max-width: 966px;
  width: 100%;
  margin: 0 auto;
  text-align: center;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.title-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 10px;
}
.riqa-logo {
  height: 88px;
}
.title-section h1 {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  line-height: 40px;
}
.description {
  color: #909399;
  margin-bottom: 40px;
}
.upload-area >>> .el-upload {
  width: 100%;
}
.upload-area >>> .el-upload-dragger {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: auto;
  padding: 40px;
  background-color: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  box-sizing: border-box;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.03);
  position: relative;
}
.upload-text-primary {
  color: #303133;
  font-size: 16px;
  margin-bottom: 15px;
}
.upload-text-secondary {
  color: #909399;
  font-size: 12px;
}
.upload-actions-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 30px;
  padding: 20px 0;
}
.custom-upload-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 20px;
  background-color: #00a0e9;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
}
.custom-upload-button:hover {
  background-color: #66b1ff;
}
.custom-upload-button i {
  font-size: 20px;
  margin-right: 8px;
}
.custom-upload-button span {
  font-size: 14px;
}
.page-footer {
  text-align: center;
  color: #909399;
  font-size: 12px;
  flex-shrink: 0;
  padding-bottom: 15px;
  padding-top: 30px;
}
.beian-link {
  color: #909399;
  text-decoration: none;
  transition: color 0.3s;
}
.beian-link:hover {
  color: #00a0e9;
  text-decoration: underline;
}
.upload-usage-hint {
  position: absolute;
  bottom: 15px;
  right: 20px;
  font-size: 12px;
  color: #909399;
  z-index: 1;
}

@media (max-width: 768px) {
  .loading-text {
    font-size: 14px;
  }
  .home-page-container {
    height: auto;
    padding-top: 0;
    flex-direction: column;
  }
  .page-aside {
    display: block;
    height: auto;
    border-right: none;
    border-bottom: 1px solid #e6ebf5;
    border-top-right-radius: 0;
    padding: 8px 10px;
    box-sizing: border-box;
    max-height: none;
    overflow: visible;
    transition: max-height 0.3s ease;
  }

  .page-aside.collapsed {
    max-height: 60px;
    overflow: hidden;
  }
  .sidebar-title {
    display: none;
  }
  .mobile-sidebar-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    padding: 0 5px 8px;
  }

  .mobile-collapse-icon {
    transform: rotate(0deg);
  }

  .mobile-collapse-icon.rotated {
    transform: rotate(180deg);
  }
  .reference-list {
    display: flex;
    overflow-x: auto;
    overflow-y: visible;
    padding: 0;
    margin: 0;
    flex: none;
    min-height: auto;
  }
  .reference-list::-webkit-scrollbar {
    display: none;
  }
  .reference-item {
    flex: 0 0 auto;
    width: 90px;
    padding: 4px;
    margin-right: 10px;
    margin-bottom: 0;
  }
  .img-wrapper {
    height: 60px;
    max-width: 100%;
    margin-bottom: 3px;
  }
  .item-title {
    width: 100%;
    padding-bottom: 3px;
  }
  .item-title span {
    font-size: 12px;
  }
  .item-title::after {
    width: 18px;
  }
  .main-content-inner {
    max-width: none;
  }
  .title-section {
    flex-direction: column;
  }
  .riqa-logo {
    height: 88px;
  }
  .title-section h1 {
    font-size: 20px;
    line-height: normal;
  }
  .description {
    font-size: 14px;
    margin-bottom: 30px;
  }
  .upload-area >>> .el-upload-dragger {
    padding: 25px;
  }
  .upload-actions-wrapper {
    flex-direction: column;
    gap: 15px;
    padding: 10px 0;
  }
  .upload-usage-hint {
    bottom: 10px;
    right: 15px;
  }
}
</style>
